import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../core/auth/auth.service';
import { extractErrorMessage } from '../core/http/api-error';
import { PkInput } from '../shared/ui/form/pk-input';
import { PkIcon } from '../shared/ui/icon/pk-icon';
import { PkSpinner } from '../shared/ui/spinner/pk-spinner';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MESSAGE,
  isValidPassword,
} from '../shared/util/account-rules';

/**
 * 다이얼로그 데이터 — `forced`면 임시 비밀번호(비밀번호 찾기) 상태의 **강제 변경**이다:
 * 안내 문구가 바뀌고 현재 비밀번호 라벨이 "임시 비밀번호"가 되며 [취소]가 없다.
 * 닫히지 않게 하는 것(`disableClose`)은 여는 쪽(`authGuard`)의 책임이다.
 */
export interface ChangePasswordDialogData {
  forced?: boolean;
}

/**
 * 본인 비밀번호 변경 다이얼로그 — GNB 사용자 블록에서 연다 (CDK Dialog, 서비스가 아니라 직접 open).
 * 현재/새/새 확인 3필드. 규칙은 사용자관리 초기화와 같은 정책(account-rules)이고,
 * 서버(`POST /auth/change-password`)가 최종 판정한다 — 현재 비밀번호 불일치 400, 정책 위반·동일 400.
 * 성공하면 true로 닫힌다 (호출부가 토스트를 낸다).
 *
 * 임시 비밀번호 강제 변경(2026-09-15)에도 같은 컴포넌트를 쓴다 — 요청·판정이 같고 문구만 다르다.
 */
@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, PkInput, PkIcon, PkSpinner],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <form
      class="cp-dialog"
      data-testid="change-password-dialog"
      [attr.data-forced]="forced ? 'true' : null"
      [formGroup]="form"
      (ngSubmit)="submit()"
    >
      <h2 class="cp-dialog__title">
        <pk-icon name="key-round" />{{ forced ? '새 비밀번호 설정' : '비밀번호 변경' }}
      </h2>
      @if (forced) {
        <p class="cp-dialog__notice" role="status" data-testid="forced-notice">
          임시 비밀번호로 로그인했습니다. 새 비밀번호로 바꾼 뒤에 다른 화면을 쓸 수 있습니다.
        </p>
      }
      <p class="cp-dialog__message">{{ passwordMessage }}</p>

      <div class="cp-dialog__fields">
        <pk-input
          [label]="forced ? '임시 비밀번호' : '현재 비밀번호'"
          type="password"
          formControlName="currentPassword"
          autocomplete="current-password"
          [maxLength]="passwordMaxLength"
          [required]="true"
        />
        <pk-input
          label="새 비밀번호"
          type="password"
          formControlName="newPassword"
          autocomplete="new-password"
          [maxLength]="passwordMaxLength"
          [required]="true"
          hint="영문·숫자·특수문자 포함 8자 이상 (공백 불가)"
        />
        <pk-input
          label="새 비밀번호 확인"
          type="password"
          formControlName="confirmPassword"
          autocomplete="new-password"
          [maxLength]="passwordMaxLength"
          [required]="true"
        />
      </div>

      @if (errorMessage(); as message) {
        <p class="cp-dialog__error" role="alert">{{ message }}</p>
      }

      <div class="cp-dialog__actions">
        @if (forced) {
          <!-- 닫을 수 없는 창의 유일한 탈출구 — 다른 계정으로 바꾸려는 사용자가 갇히지 않게 -->
          <button
            type="button"
            class="btn cp-dialog__btn cp-dialog__btn--logout"
            data-testid="forced-logout"
            [disabled]="busy()"
            (click)="logout()"
          >
            <pk-icon name="log-out" [size]="14" />로그아웃
          </button>
        } @else {
          <button type="button" class="btn cp-dialog__btn" [disabled]="busy()" (click)="cancel()">
            취소
          </button>
        }
        <button type="submit" class="btn cp-dialog__btn cp-dialog__btn--confirm" [disabled]="busy()">
          @if (busy()) {
            <pk-spinner />변경 중...
          } @else {
            변경
          }
        </button>
      </div>
    </form>
  `,
  styles: `
    .cp-dialog {
      width: 420px;
      max-width: calc(100vw - 48px);
      padding: var(--pk-space-5) var(--pk-space-6);
      background: #fff;
      border-radius: var(--pk-radius);
      box-shadow: var(--pk-shadow-lg);
      box-sizing: border-box;
    }

    .cp-dialog__title {
      margin: 0 0 var(--pk-space-2);
      font-size: 16px;
      font-weight: 700;
      color: var(--pk-text);
    }

    /* 강제 변경 안내 — 왜 이 창이 닫히지 않는지를 먼저 말한다 */
    .cp-dialog__notice {
      margin: 0 0 var(--pk-space-3);
      padding: 8px 12px;
      border-radius: var(--pk-radius-sm);
      background: var(--pk-warning-soft);
      color: var(--pk-text);
      font-size: 13px;
      line-height: 1.6;
    }

    .cp-dialog__message {
      margin: 0 0 var(--pk-space-4);
      font-size: 13px;
      line-height: 1.6;
      color: var(--pk-text-sub);
    }

    .cp-dialog__fields {
      display: flex;
      flex-direction: column;
      gap: var(--pk-space-3);
    }

    .cp-dialog__error {
      margin: var(--pk-space-3) 0 0;
      padding: 8px 12px;
      border-radius: var(--pk-radius-sm);
      background: var(--pk-danger-soft);
      color: var(--pk-danger);
      font-size: 13px;
      line-height: 1.5;
    }

    .cp-dialog__actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--pk-space-2);
      margin-top: var(--pk-space-5);
    }

    .cp-dialog__btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      border: 1px solid var(--pk-border);
      border-radius: var(--pk-radius-sm);
      background: #fff;
      color: var(--pk-text);
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: var(--pk-bg);
      }

      &:disabled {
        color: var(--pk-text-muted);
        cursor: default;
      }
    }

    .cp-dialog__btn--logout {
      margin-right: auto;
      color: var(--pk-text-sub);
    }

    .cp-dialog__btn--confirm {
      border-color: var(--pk-green-dark);
      background: var(--pk-green);
      color: #fff;

      &:hover:not(:disabled) {
        background: var(--pk-green-dark);
      }

      &:disabled {
        border-color: var(--pk-border);
        background: #eef1ef;
      }
    }
  `,
})
export class ChangePasswordDialog {
  private readonly ref = inject<DialogRef<boolean>>(DialogRef);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(NonNullableFormBuilder);
  /** GNB에서 열 때는 데이터 없이 연다 — optional */
  private readonly data = inject<ChangePasswordDialogData | null>(DIALOG_DATA, { optional: true });

  protected readonly forced = this.data?.forced === true;
  protected readonly passwordMessage = PASSWORD_MESSAGE;
  protected readonly passwordMaxLength = PASSWORD_MAX_LENGTH;

  protected readonly busy = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly form = this.fb.group({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  /**
   * 강제 변경 창은 `disableClose`라 세션이 끊겨도(만료·다른 탭 로그아웃·[로그아웃]) 스스로 남는다 —
   * 로그인 화면 위에 닫히지 않는 모달이 얹히므로 세션 소거를 보고 여기서 닫는다(리뷰 W1).
   * false로 닫히면 가드는 플래그를 건드리지 않는다(다음 로그인이 새 값을 가져온다).
   */
  private readonly sessionWatch = effect(() => {
    if (this.forced && !this.auth.isAuthenticated()) {
      this.ref.close(false);
    }
  });

  protected submit(): void {
    if (this.busy()) {
      return;
    }
    const { currentPassword, newPassword, confirmPassword } = this.form.getRawValue();
    if (currentPassword === '' || newPassword === '' || confirmPassword === '') {
      this.errorMessage.set(
        this.forced
          ? '임시 비밀번호, 새 비밀번호, 새 비밀번호 확인을 모두 입력하세요.'
          : '현재 비밀번호, 새 비밀번호, 새 비밀번호 확인을 모두 입력하세요.',
      );
      return;
    }
    if (!isValidPassword(newPassword)) {
      this.errorMessage.set(PASSWORD_MESSAGE);
      return;
    }
    if (newPassword !== confirmPassword) {
      this.errorMessage.set('새 비밀번호와 새 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    if (newPassword === currentPassword) {
      this.errorMessage.set(
        this.forced
          ? '새 비밀번호가 임시 비밀번호와 같습니다.'
          : '새 비밀번호가 현재 비밀번호와 같습니다.',
      );
      return;
    }
    this.busy.set(true);
    this.errorMessage.set(null);
    this.auth.changePassword({ currentPassword, newPassword }).subscribe({
      next: () => {
        this.busy.set(false);
        this.ref.close(true);
      },
      // 현재 비밀번호 불일치 400·정책 위반 400 — 서버 메시지를 그대로 다이얼로그 안에 표시
      error: (err: unknown) => {
        this.busy.set(false);
        this.errorMessage.set(extractErrorMessage(err, '비밀번호 변경 중 오류가 발생했습니다.'));
        if (this.forced && err instanceof HttpErrorResponse && err.status === 400) {
          void this.closeIfAlreadyChanged();
        }
      },
    });
  }

  /**
   * 다른 탭에서 이미 바꿨을 수 있다(리뷰 INFO 10) — 탭 B가 변경을 끝내면 임시 비밀번호는 더
   * 이상 맞지 않아 이 탭의 요청은 400이 되는데, 이 탭의 메모리 플래그는 그대로라 창이 남는다.
   * `/auth/me`를 다시 읽어 서버가 이미 해제했으면 성공과 같은 길(true)로 닫는다.
   */
  private async closeIfAlreadyChanged(): Promise<void> {
    const refreshed = await this.auth.refreshMenus();
    if (refreshed && this.auth.user()?.mustChangePassword === false) {
      this.ref.close(true);
    }
  }

  /** 강제 변경 창의 [로그아웃] — 세션을 끝내면 `sessionWatch`가 창을 닫고 가드가 /login으로 보낸다 */
  protected logout(): void {
    this.auth.logout();
  }

  protected cancel(): void {
    this.ref.close(undefined);
  }
}
